import { useState, useCallback, useMemo } from 'react';

export type PopoverStateConfig = {
    initialOpen?: boolean;
    preventDefault?: boolean;
    stopPropagation?: boolean;
    anchor?: React.MutableRefObject<HTMLElement | null>;
};

export type PopoverState = {
    open: boolean;
    onRequestOpen: (event?: React.MouseEvent) => void;
    onRequestClose: () => void;
};

export const usePopoverState = ({
    initialOpen = false,
    preventDefault = false,
    stopPropagation = false,
    anchor,
}: PopoverStateConfig = {}): PopoverState => {
    const [open, setOpen] = useState(initialOpen);

    const onRequestOpen = useCallback<PopoverState['onRequestOpen']>(
        (event) => {
            if (preventDefault) {
                event?.preventDefault();
            }

            if (stopPropagation) {
                event?.stopPropagation();
            }

            setOpen(true);
        },
        [preventDefault, stopPropagation]
    );

    const onRequestClose = useCallback<PopoverState['onRequestClose']>(() => {
        setOpen(false);
        if (anchor?.current) {
            anchor.current.focus();
        }
    }, [anchor]);

    return useMemo(
        () => ({ open, onRequestOpen, onRequestClose }),
        [open, onRequestOpen, onRequestClose]
    );
};
